import getLineLocalId from '@gaia/utils/gaia/getLineLocalId';
import convertLngLat from '@gaia/utils/mapbox/convertLngLat';

import fetchRoutedLine, {routedLineCache} from './fetchRoutedLine';
import getStraightLine from './getStraightLine';

interface GetLineOptions {
    fromPoint: TrackPoint;
    draggingLocalId: string | undefined;
    project: MapProject;
    toPoint: TrackPoint;
    track: Track;
}

export default function getNormalRequest({
    fromPoint,
    draggingLocalId,
    project,
    toPoint,
    track,
}: GetLineOptions): TrackLineRequest {
    if (!track.isGeometryLoaded) throw new Error();

    const {
        unmodifiedLines,
        properties: {id, localId, color, routing_mode: costing},
    } = track;

    const isDragging =
        fromPoint.properties.localId === draggingLocalId ||
        toPoint.properties.localId === draggingLocalId;

    const fromLngLat = convertLngLat(fromPoint.geometry.coordinates);
    const toLngLat = convertLngLat(toPoint.geometry.coordinates);
    const lineLocalId = getLineLocalId(localId, fromPoint, toPoint);

    const properties = {
        fromPointLocalId: fromPoint.properties.localId,
        lineLocalId,
        toPointLocalId: toPoint.properties.localId,
        localId,
        color,
        id,
        routing_mode: track.properties.routing_mode,
    };

    const isGeometryModified =
        fromPoint.properties.isGeometryModified || toPoint.properties.isGeometryModified;

    if (isDragging) {
        return {
            type: 'sync',
            properties: {...properties, type: 'preview'},
            geometry: getStraightLine(fromLngLat, toLngLat),
        };
    } else if (unmodifiedLines && unmodifiedLines[lineLocalId] && !isGeometryModified) {
        return {
            type: 'sync',
            geometry: unmodifiedLines[lineLocalId].geometry,
            properties: {...properties, type: 'normal'},
        };
    } else {
        const cacheKey = JSON.stringify([fromLngLat.toArray(), toLngLat.toArray(), costing, true]);

        return {
            type: 'async',
            properties: {...properties, type: 'normal'},
            loadingGeometry: routedLineCache.get(cacheKey) || getStraightLine(fromLngLat, toLngLat),
            geometry: fetchRoutedLine({costing, fromLngLat, project, toLngLat}),
        };
    }
}
